import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Animated,
  Dimensions,
  Easing,
  View,
  type LayoutChangeEvent,
} from 'react-native'

import Svg, { Path } from 'react-native-svg'

import type { MaskProps, SvgMaskPathFunction, ValueXY } from '../types'

const AnimatedSvgPath = Animated.createAnimatedComponent(Path)
const windowDimensions = Dimensions.get('window')

const defaultSvgPath: SvgMaskPathFunction = ({
  size,
  position,
  canvasSize,
}): string => {
  const br = 4 // border radius
  const sizeX = size.x._value - 1.8 * br
  const sizeY = size.y._value - 1.8 * br

  const _sizeX = 0 - sizeX
  const _sizeY = 0 - sizeY

  return `M 0 0 H ${canvasSize.x} V ${canvasSize.y} H 0 V 0 Z M ${
    position.x._value + br
  } ${
    position.y._value
  } Z h ${sizeX} a ${br} ${br} 0 0 1 ${br} ${br} v ${sizeY} a ${br} ${br} 0 0 1 -${br} ${br} h ${_sizeX} a ${br} ${br} 0 0 1 -${br} -${br} v ${_sizeY} a ${br} ${br} 0 0 1 ${br} -${br} z`
}

export const SvgMask = ({
  size,
  position,
  style,
  easing = Easing.linear,
  animationDuration = 300,
  animated,
  backdropColor,
  svgMaskPath = defaultSvgPath,
  onClick,
  currentStep,
}: MaskProps) => {
  const [canvasSize, setCanvasSize] = useState<ValueXY>({
    x: windowDimensions.width,
    y: windowDimensions.height,
  })
  const sizeValue = useRef<Animated.ValueXY>(new Animated.ValueXY(size)).current
  const positionValue = useRef<Animated.ValueXY>(
    new Animated.ValueXY(position)
  ).current
  const maskRef = useRef<any>(null)

  const animationListener = useCallback(() => {
    const d: string = svgMaskPath({
      size: sizeValue,
      position: positionValue,
      canvasSize,
      step: currentStep,
    })

    if (maskRef.current) {
      maskRef.current.setNativeProps({ d })
    }
  }, [canvasSize, currentStep, svgMaskPath, positionValue, sizeValue])

  const animate = useCallback(
    (toSize: ValueXY = size, toPosition: ValueXY = position) => {
      if (animated) {
        Animated.parallel([
          Animated.timing(sizeValue, {
            toValue: toSize,
            duration: animationDuration,
            easing,
            useNativeDriver: false,
          }),
          Animated.timing(positionValue, {
            toValue: toPosition,
            duration: animationDuration,
            easing,
            useNativeDriver: false,
          }),
        ]).start()
      } else {
        sizeValue.setValue(toSize)
        positionValue.setValue(toPosition)
      }
    },
    [
      animated,
      animationDuration,
      easing,
      positionValue,
      position,
      size,
      sizeValue,
    ]
  )

  useEffect(() => {
    const id = positionValue.addListener(animationListener)
    return () => {
      positionValue.removeListener(id)
    }
  }, [animationListener, positionValue])

  useEffect(() => {
    if (size && position) {
      animate(size, position)
    }
  }, [animate, position, size])

  const handleLayout = ({
    nativeEvent: {
      layout: { width, height },
    },
  }: LayoutChangeEvent) => {
    setCanvasSize({
      x: width,
      y: height,
    })
  }

  return (
    <View
      style={style}
      onLayout={handleLayout}
      onStartShouldSetResponder={onClick}
    >
      {canvasSize ? (
        <Svg pointerEvents="none" width={canvasSize.x} height={canvasSize.y}>
          <AnimatedSvgPath
            ref={maskRef}
            fill={backdropColor}
            fillRule="evenodd"
            strokeWidth={1}
            d={svgMaskPath({
              size: sizeValue,
              position: positionValue,
              canvasSize,
              step: currentStep,
            })}
          />
        </Svg>
      ) : null}
    </View>
  )
}
